/**
 * Formateo y parseo de valores segun la metadata.
 *
 * Un solo lugar decide como se ve un numero, una fecha o un monto en toda la
 * aplicacion. Dos decisiones que conviene conocer:
 *
 * 1. Los decimales llegan del backend como **string** (`_to_json` los serializa
 *    asi a proposito, para no perder precision al pasar por `float`). Aca se
 *    formatean para mostrar, pero el valor que se guarda vuelve a viajar como
 *    string: nunca se lo convierte a numero en el camino de escritura.
 * 2. La localizacion es `es-AR` fija. No se toma del navegador: el mismo importe
 *    tiene que leerse igual en la pantalla de la administracion y en el PDF que
 *    firma, y el PDF lo hace el backend.
 */

import type { FieldMeta, FieldValue, JsonValue, Many2OneValue } from '@/lib/types';

export const LOCALE = 'es-AR';

const MONEDA_POR_DEFECTO = 'ARS';

// ---------------------------------------------------------------------------
// Guardas de tipo
// ---------------------------------------------------------------------------
/** `true` si el valor es un many2one serializado (`{id, display_name}`). */
export function isMany2One(valor: unknown): valor is Many2OneValue {
  return (
    typeof valor === 'object' &&
    valor !== null &&
    !Array.isArray(valor) &&
    typeof (valor as { id?: unknown }).id === 'number'
  );
}

/** Ids de un x2many. Acepta la lista de ids que manda el backend. */
export function toIdList(valor: FieldValue): number[] {
  if (!Array.isArray(valor)) return [];
  const salida: number[] = [];
  for (const entrada of valor) {
    if (typeof entrada === 'number') salida.push(entrada);
    else if (isMany2One(entrada)) salida.push(entrada.id);
  }
  return salida;
}

/**
 * Filas de una grilla hija cargadas EN MEMORIA, todavía sin guardar.
 *
 * El valor de un `one2many` tiene dos formas según en qué estado esté el
 * registro que lo contiene, y las dos son legítimas:
 *
 * - en un registro **guardado** es la lista de **ids** de los hijos, que es como
 *   la serializa la API;
 * - en un registro **nuevo** es la lista de **filas** que se cargaron antes de
 *   guardar (`One2ManyField` las acumula ahí y `FormView` las manda anidadas en
 *   el POST, para que padre e hijos entren en una sola transacción).
 *
 * Se distinguen por la forma —objeto vs. número— y no por una bandera aparte.
 * Cualquier otra cosa se descarta en vez de romper: esto lo leen la grilla y el
 * formulario, y ninguno de los dos puede caerse por un valor inesperado.
 */
export function toDraftRows(valor: FieldValue): Record<string, FieldValue>[] {
  if (!Array.isArray(valor)) return [];
  const salida: Record<string, FieldValue>[] = [];
  for (const fila of valor) {
    if (typeof fila === 'object' && fila !== null && !Array.isArray(fila)) {
      salida.push(fila as Record<string, FieldValue>);
    }
  }
  return salida;
}

/** `true` cuando el campo no tiene contenido, a los fines de "requerido". */
export function isEmpty(valor: FieldValue): boolean {
  if (valor === null || valor === undefined) return true;
  if (typeof valor === 'string') return valor.trim() === '';
  if (Array.isArray(valor)) return valor.length === 0;
  return false;
}

// ---------------------------------------------------------------------------
// Numeros
// ---------------------------------------------------------------------------
function decimales(meta: FieldMeta): number {
  const digits = meta.digits;
  if (Array.isArray(digits) && typeof digits[1] === 'number') return digits[1];
  return meta.type === 'integer' ? 0 : 2;
}

function aNumero(valor: FieldValue): number | null {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  if (typeof valor === 'string' && valor.trim() !== '') {
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : null;
  }
  return null;
}

/**
 * `false` si el campo declara `options.grouping: false`.
 *
 * Es GENERICO: un entero que no es una cantidad —un año, un numero de
 * documento, un codigo postal, un legajo— no lleva separador de miles. Sin esto
 * el formulario del periodo de expensas mostraba el año **2.026**, que no es un
 * año sino un error de formato mirando al usuario.
 */
export function useGrouping(meta: FieldMeta): boolean {
  return meta.options?.['grouping'] !== false;
}

export function formatNumber(valor: FieldValue, meta: FieldMeta): string {
  const numero = aNumero(valor);
  if (numero === null) return '';
  const fraccion = decimales(meta);
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: fraccion,
    maximumFractionDigits: fraccion,
    useGrouping: useGrouping(meta),
  }).format(numero);
}

/**
 * Importe en pesos (o la moneda pedida) con formato es-AR: `$ 1.234.567,89`.
 *
 * Es el ladrillo de moneda de TODA la app. `formatMonetary` (que saca los
 * decimales de la metadata) y cualquier panel que muestre plata sin `FieldMeta`
 * a mano —la agenda de vencimientos, el tablero de pendientes— pasan por aca,
 * asi que el separador de miles y la coma decimal se deciden en un solo lugar.
 */
export function formatCurrency(
  valor: FieldValue,
  opciones: {
    currency?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string {
  const numero = aNumero(valor);
  if (numero === null) return '';
  const moneda = opciones.currency ?? MONEDA_POR_DEFECTO;
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: moneda,
      ...(opciones.minimumFractionDigits === undefined
        ? {}
        : { minimumFractionDigits: opciones.minimumFractionDigits }),
      ...(opciones.maximumFractionDigits === undefined
        ? {}
        : { maximumFractionDigits: opciones.maximumFractionDigits }),
    }).format(numero);
  } catch {
    // Codigo de moneda invalido: se muestra el numero antes que romper la pantalla.
    return new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: opciones.minimumFractionDigits ?? 2,
      maximumFractionDigits: opciones.maximumFractionDigits ?? 2,
    }).format(numero);
  }
}

export function formatMonetary(valor: FieldValue, meta: FieldMeta): string {
  const opciones = meta.options ?? {};
  const moneda = typeof opciones.currency === 'string' ? opciones.currency : MONEDA_POR_DEFECTO;
  return formatCurrency(valor, {
    currency: moneda,
    minimumFractionDigits: decimales(meta),
    maximumFractionDigits: decimales(meta),
  });
}

/** Unidad de medida declarada por el modulo. `m²` es el caso del widget `area`. */
export function unitOf(meta: FieldMeta, porDefecto: string): string {
  const opciones = meta.options ?? {};
  return typeof opciones.unit === 'string' && opciones.unit !== '' ? opciones.unit : porDefecto;
}

/**
 * Magnitud con unidad: `84,50 m²`.
 *
 * Se usa un espacio duro entre el numero y la unidad para que no se corten en
 * dos lineas dentro de una celda angosta.
 */
export function formatUnit(valor: FieldValue, meta: FieldMeta, porDefecto = 'm²'): string {
  const texto = formatNumber(valor, meta);
  if (texto === '') return '';
  return `${texto} ${unitOf(meta, porDefecto)}`;
}

/**
 * Horas decimales a `2 h 30 min`.
 *
 * El modelo guarda `2.50` (sumar y facturar horas es aritmetica decimal) pero
 * nadie lee "2,50 horas". Se muestran los minutos solo cuando los hay, para que
 * una jornada de 8 horas no diga "8 h 0 min".
 */
export function formatHours(valor: FieldValue): string {
  const numero = aNumero(valor);
  if (numero === null) return '';
  const total = Math.round(Math.abs(numero) * 60);
  const horas = Math.floor(total / 60);
  const minutos = total % 60;
  const signo = numero < 0 ? '−' : '';
  const conMiles = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }).format(horas);
  if (horas === 0 && minutos === 0) return '0 h';
  if (minutos === 0) return `${signo}${conMiles} h`;
  if (horas === 0) return `${signo}${minutos} min`;
  return `${signo}${conMiles} h ${minutos} min`;
}

/**
 * Una tasa como la escribe una persona: `2`, no `2.000000`.
 *
 * Una tasa se guarda con varios decimales —un porcentaje puede necesitar ser
 * exacto hasta el sexto— y quien la mostraba tal cual escribia «2.000000 %», con
 * el punto decimal que no es el nuestro y una cola de ceros que no significa
 * nada. Se sacan SOLO los ceros de la cola: `2,50` sigue siendo `2,5` y
 * `0,050000` es `0,05`. No se redondea, porque redondear una tasa es cambiarla.
 *
 * Es el espejo de `nucleo.core.tabular.formatear_porcentaje`, que hace lo mismo
 * del lado del backend para los documentos impresos.
 */
export function formatRate(valor: FieldValue): string {
  if (valor === null || valor === undefined || valor === '') return '';
  const texto = String(valor).trim();
  if (!/^-?\d+(\.\d+)?$/.test(texto)) return texto;
  // Se trabaja sobre el TEXTO y no sobre un `number`: el decimal viene del
  // backend como string a proposito y pasarlo por coma flotante para despues
  // volver a texto es la clase de viaje que introduce un `2.0000000000000004`.
  const [entero = '0', fraccion = ''] = texto.split('.');
  const limpia = fraccion.replace(/0+$/, '');
  const conMiles = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }).format(
    Number(entero),
  );
  return limpia === '' ? conMiles : `${conMiles},${limpia}`;
}

export function formatPercent(valor: FieldValue, meta: FieldMeta): string {
  const numero = aNumero(valor);
  if (numero === null) return '';
  const opciones = meta.options ?? {};
  // Un coeficiente de expensas se guarda como 0.0345 o como 3.45 segun el
  // modulo; `factor` dice cual de las dos cosas es.
  const factor = typeof opciones.factor === 'number' ? opciones.factor : 1;
  return `${formatNumber(numero * factor, meta)} %`;
}

// ---------------------------------------------------------------------------
// Fechas
// ---------------------------------------------------------------------------
/** Fecha ISO (`2026-07-28`) a `28/07/2026`, sin pasar por `Date` (evita el corrimiento de zona). */
export function formatDate(valor: FieldValue): string {
  if (typeof valor !== 'string' || valor === '') return '';
  const partes = valor.slice(0, 10).split('-');
  const [anio, mes, dia] = partes;
  if (anio === undefined || mes === undefined || dia === undefined) return valor;
  return `${dia}/${mes}/${anio}`;
}

/**
 * Dias enteros entre hoy (medianoche local) y una fecha ISO `AAAA-MM-DD`.
 * Negativo = ya paso; `0` = hoy. Sin pasar por `Date(valor)` para no arrastrar
 * el corrimiento de zona.
 */
export function daysUntil(iso: FieldValue): number | null {
  if (typeof iso !== 'string' || iso === '') return null;

  // Hay que distinguir dos cosas que llegan por el mismo parámetro:
  //
  // * una FECHA (`2026-09-10`) — un vencimiento. No tiene hora ni zona: el 10
  //   de septiembre es el 10 de septiembre acá y en Tokio, así que se arma
  //   directamente a medianoche LOCAL. Convertirla desde UTC la correría un día.
  // * una FECHA Y HORA (`2026-08-04T00:24:31Z`) — cuándo se escribió un mensaje
  //   del historial. Ahí el instante sí tiene zona, y quedarse con los diez
  //   primeros caracteres es leer el día **de UTC**: un mensaje escrito a las
  //   21:24 en Buenos Aires ya es del día siguiente en UTC, y el hilo lo
  //   mostraba como «mañana». Se deja que `Date` lo interprete y se compara el
  //   día del calendario local, que es el que ve la persona.
  let objetivo: Date;
  if (iso.includes('T')) {
    const instante = new Date(iso);
    if (Number.isNaN(instante.getTime())) return null;
    objetivo = new Date(instante.getFullYear(), instante.getMonth(), instante.getDate());
  } else {
    const partes = iso.slice(0, 10).split('-');
    const anio = Number.parseInt(partes[0] ?? '', 10);
    const mes = Number.parseInt(partes[1] ?? '', 10);
    const dia = Number.parseInt(partes[2] ?? '', 10);
    if (!Number.isFinite(anio) || !Number.isFinite(mes) || !Number.isFinite(dia)) return null;
    objetivo = new Date(anio, mes - 1, dia);
  }

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return Math.round((objetivo.getTime() - hoy.getTime()) / 86_400_000);
}

/**
 * Fecha relativa en es-AR: «hoy», «ayer», «mañana», «hace 6 días», «en 2 días».
 *
 * Usa `Intl.RelativeTimeFormat` con `numeric: 'auto'` para que ±1 salga como
 * palabra («ayer»/«mañana») y no como «hace 1 día». Es el mismo criterio en toda
 * la app: la agenda de vencimientos y cualquier «hace/en N días» pasan por aca.
 */
export function formatRelativeDate(iso: FieldValue): string {
  const dias = daysUntil(iso);
  if (dias === null) return '';
  return new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' }).format(dias, 'day');
}

// ---------------------------------------------------------------------------
// Entrada de fecha dd/mm/aaaa (para el date picker propio)
// ---------------------------------------------------------------------------
/**
 * Enmascara lo que se escribe a `dd/mm/aaaa` a medida que se tipea: toma los
 * digitos y les intercala las barras. `"2807"` -> `"28/07"`, `"28072026"` ->
 * `"28/07/2026"`.
 */
export function maskDMY(crudo: string): string {
  const digitos = crudo.replace(/\D/g, '').slice(0, 8);
  const grupos: string[] = [];
  grupos.push(digitos.slice(0, 2));
  if (digitos.length > 2) grupos.push(digitos.slice(2, 4));
  if (digitos.length > 4) grupos.push(digitos.slice(4, 8));
  return grupos.filter((g) => g !== '').join('/');
}

/**
 * `dd/mm/aaaa` a ISO `AAAA-MM-DD`, o `null` si no es una fecha real. Rechaza
 * `31/02/2026` y cualquier cosa incompleta: el date picker propio solo emite a
 * la API cuando lo que hay es una fecha valida.
 */
export function isoFromDMY(texto: string): string | null {
  const m = texto.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m === null) return null;
  const dia = Number.parseInt(m[1] as string, 10);
  const mes = Number.parseInt(m[2] as string, 10);
  const anio = Number.parseInt(m[3] as string, 10);
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
  // `Date` local valida que el dia exista en ese mes (rechaza 31/02, 30/02, etc.).
  const fecha = new Date(anio, mes - 1, dia);
  if (fecha.getFullYear() !== anio || fecha.getMonth() + 1 !== mes || fecha.getDate() !== dia) {
    return null;
  }
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export function formatDateTime(valor: FieldValue): string {
  if (typeof valor !== 'string' || valor === '') return '';
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return valor;
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(fecha);
}

/** ISO con zona a `YYYY-MM-DDTHH:mm`, que es lo que acepta `<input type="datetime-local">`. */
export function toDateTimeLocal(valor: FieldValue): string {
  if (typeof valor !== 'string' || valor === '') return '';
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return '';
  const desplazado = new Date(fecha.getTime() - fecha.getTimezoneOffset() * 60_000);
  return desplazado.toISOString().slice(0, 16);
}

/** Lo que escribio el usuario en un `datetime-local`, de vuelta a ISO con zona. */
export function fromDateTimeLocal(texto: string): string | null {
  if (texto === '') return null;
  const fecha = new Date(texto);
  if (Number.isNaN(fecha.getTime())) return null;
  return fecha.toISOString();
}

// ---------------------------------------------------------------------------
// Texto de un valor cualquiera
// ---------------------------------------------------------------------------
/** Etiqueta de una opcion de `choices`. Cae al valor crudo si no esta declarada. */
export function choiceLabel(meta: FieldMeta, valor: JsonValue): string {
  const opcion = meta.choices?.find((c) => c.value === valor);
  if (opcion !== undefined) return opcion.label;
  return valor === null || valor === undefined ? '' : String(valor);
}

/**
 * Etiqueta declarada en `options.labels` para un campo SIN `choices`.
 *
 * Existe para el caso en que la columna es generica y el vocabulario lo pone
 * otro modulo: `base.Partner.client_kind` guarda `"consorcio"` y es `base` quien
 * declara la columna, pero `base` no sabe que es un consorcio —lo sabe el
 * vertical, y por eso el campo no puede declarar `choices` sin importar
 * vocabulario ajeno—. Sin esto la pastilla mostraba el codigo crudo, que es como
 * se escribe en la base y no como lo dice una persona.
 *
 * Devuelve `null` cuando no hay etiqueta declarada, para que el llamador siga su
 * camino normal en vez de recibir un texto inventado.
 */
function labelDeclarado(meta: FieldMeta, valor: JsonValue): string | null {
  const declarados = meta.options?.labels;
  if (declarados === null || typeof declarados !== 'object' || Array.isArray(declarados)) return null;
  const propuesto = (declarados as Record<string, JsonValue>)[String(valor)];
  return typeof propuesto === 'string' ? propuesto : null;
}

/**
 * Representacion textual de cualquier valor. La usan la lista, el kanban, la
 * exportacion y los campos de solo lectura: si algo se ve distinto en dos
 * lugares, es un bug de acá y no de la pantalla.
 */
export function displayText(meta: FieldMeta, valor: FieldValue): string {
  if (valor === null || valor === undefined) return '';

  switch (meta.type) {
    case 'many2one':
      return isMany2One(valor) ? valor.display_name : String(valor);
    case 'one2many':
    case 'many2many': {
      const ids = toIdList(valor);
      return ids.length === 0 ? '' : `${ids.length}`;
    }
    case 'boolean':
      return valor === true ? 'Sí' : 'No';
    case 'date':
      return formatDate(valor);
    case 'datetime':
      return formatDateTime(valor);
    case 'json':
      return JSON.stringify(valor);
    default:
      break;
  }

  if (meta.choices !== undefined && meta.choices.length > 0) {
    return choiceLabel(meta, valor as JsonValue);
  }

  // Sin `choices` pero con etiquetas declaradas: la columna es generica y el
  // vocabulario lo puso otro modulo. Va despues de `choices` porque un campo que
  // enumera sus valores ya dice como se escriben.
  const declarado = labelDeclarado(meta, valor as JsonValue);
  if (declarado !== null) return declarado;

  const widget = meta.widget;
  if (widget === 'money' || widget === 'monetary') return formatMonetary(valor, meta);
  if (widget === 'percent') return formatPercent(valor, meta);
  if (widget === 'area') return formatUnit(valor, meta);

  if (meta.type === 'integer' || meta.type === 'float' || meta.type === 'decimal') {
    return formatNumber(valor, meta);
  }

  return String(valor);
}
